import { drawText, textWidth } from './art.ts';

export const MATERIAL = {
  bone: ['#322725', '#5c4b43', '#b19b7f', '#e6d6b8', '#f6ecd6'],
  stone: ['#18171e', '#38343f', '#77727a', '#a7a1a2', '#e7e2d6'],
  bronze: ['#2d2020', '#634335', '#9e6d48', '#c9945b', '#efc887'],
} as const;

/** Chamfered, five-tone slabs. The bevel and cast shadow are separate from the inset. */
export function slab(g, x: number, y: number, w: number, h: number, material: keyof typeof MATERIAL = 'bone', inset?: string) {
  const [ink, shade, mid, face, light] = MATERIAL[material];
  const band = (xx: number, yy: number, ww: number, hh: number, c: string, cut = 2) => {
    g.fillStyle = c;
    for (let row = 0; row < hh; row++) {
      const indent = Math.max(0, cut - Math.min(row, hh - 1 - row));
      g.fillRect(xx + indent, yy + row, ww - indent * 2, 1);
    }
  };
  band(x + 1, y + 3, w, h, '#08070b', 3);
  band(x, y, w, h, ink, 3);
  band(x + 1, y + 1, w - 2, h - 2, shade, 2);
  band(x + 1, y + 1, w - 3, h - 4, mid, 2);
  band(x + 2, y + 2, w - 5, h - 5, face, 1);
  g.fillStyle = light; g.fillRect(x + 3, y + 1, w - 7, 1); g.fillRect(x + 1, y + 3, 1, h - 7);
  if (inset) {
    band(x + 4, y + 4, w - 8, h - 8, shade, 1);
    band(x + 5, y + 5, w - 10, h - 10, inset, 1);
    g.fillStyle = mid; g.fillRect(x + 5, y + h - 5, w - 10, 1);
  }
  // Sparse edge cuts, kept away from the text and the contents of the well.
  for (let dx = 13; dx < w - 7; dx += 23) {
    g.fillStyle = shade; g.fillRect(x + dx, y + 1, 1, 2);
    g.fillStyle = light; g.fillRect(x + dx + 1, y + 1, 1, 1);
  }
}

export function centered(g, label: string, x: number, y: number, width: number, color: string) {
  drawText(g, label, x + Math.floor((width - textWidth(label)) / 2), y, color);
}

const SMALL: Record<string, string> = {
  A:'010101111101101', B:'110101110101110', C:'011100100100011', D:'110101101101110',
  E:'111100110100111', F:'111100110100100', G:'011100101101011', H:'101101111101101',
  I:'111010010010111', J:'001001001101010', K:'101101110101101', L:'100100100100111',
  M:'101111111101101', N:'101111111111101', O:'010101101101010', P:'110101110100100',
  Q:'010101101111011', R:'110101110101101', S:'011100010001110', T:'111010010010010',
  U:'101101101101111', V:'101101101101010', W:'101101111111101', X:'101101010101101',
  Y:'101101010010010', Z:'111001010100111', '0':'111101101101111', '1':'010110010010111',
  '2':'110001010100111', '3':'110001010001110', '4':'101101111001001', '5':'111100110001110',
  '6':'011100110101010', '7':'111001010010010', '8':'010101010101010', '9':'010101011001110',
  '/':'001001010100100', ':':'000010000010000', '-':'000000111000000', '.':'000000000000010',
  '+':'000010111010000', '?':'110001010000010', ' ':'000000000000000',
  "'":'010010000000000', ',':'000000000010100', ';':'000010000010100',
};

/** A native 3x5 face for secondary labels; no resampling of the main font. */
export function smallText(g, label: string, x: number, y: number, color: string, maxWidth = Infinity) {
  const limit = Math.max(0, Math.floor((maxWidth + 1) / 4));
  let value = label;   // lower case, like everything else (2026-09-10)
  if (value.length > limit) value = value.slice(0, Math.max(0, limit - 2)) + '..';
  g.fillStyle = color;
  for (let i = 0; i < value.length; i++) {
    /* THE THREE-PIXEL FACE IS SMALL CAPS, and that is not a shout (2026-09-10).
     * Everything the game says is lower case now, and this face has fifteen
     * pixels a letter: an 'a' with a bowl, an 'h' with an ascender and a 'p'
     * with a descender cannot exist in 3x5, which is why the table was
     * uppercase-only in the first place. Lowering the strings without this
     * line drew the country as `?????`. The letterforms fall back to caps and
     * the words stay lower case wherever they are read at full size. */
    const pixels = SMALL[value[i]] || SMALL[value[i].toUpperCase()] || SMALL['?'];
    for (let p = 0; p < 15; p++) if (pixels[p] === '1') g.fillRect(x + i * 4 + p % 3, y + Math.floor(p / 3), 1, 1);
  }
}
